<script>
  /**
   * SelectionControls — the users toolbar's selection cluster.
   *
   * Off: a single Select toggle. On: the selected count, Select All, an Actions
   * dropdown, and Cancel. The count and Actions hide at zero selected; Select All
   * and Cancel are always present.
   *
   * The count reads against `total` — the users matching the current filters, not
   * the current page — because selection persists across pages. Select All adds
   * only the current page; there is no select-all-matching, because Clear Cache
   * and Disable are one request per user and would take minutes at that scale.
   *
   * Once the selection covers `total` the button becomes Deselect All and clears
   * everything. Reaching that takes one click when the result set fits on a page,
   * or one per page otherwise.
   *
   * @example
   * <SelectionControls
   *   bind:selecting
   *   {selection}
   *   total={totalRecords}
   *   id="users"
   *   onAction={onBulkAction}
   *   onSelectAll={() => selection.add(filtered)}
   * />
   */

  import { format } from 'svelte-i18n';
  import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  /**
   * Whether selection mode is active. Use `bind:selecting`.
   * @type {boolean}
   */
  export let selecting = false;

  /**
   * A selection store from `createItemSelection()`, keyed by email.
   * @type {{ subscribe: Function, toggle: Function, selectAll: Function, clear: Function }}
   */
  export let selection;

  /**
   * How many users match the current filters. The denominator of the count.
   * @type {number}
   */
  export let total = 0;

  /**
   * Called with the chosen action when a dropdown item is clicked.
   * @type {(action: 'reminder' | 'cache' | 'disable' | 'signatures') => void}
   */
  export let onAction = () => {};

  /**
   * Called when Select All is clicked. The caller adds the users currently on
   * screen; this component deliberately knows nothing about them.
   * @type {() => void}
   */
  export let onSelectAll = () => {};

  /**
   * Prefix for tooltip target ids, so several instances can coexist.
   * @type {string}
   */
  export let id = 'users';

  let showMenu = false;

  const enable = () => {
    selecting = true;
  };

  const cancel = () => {
    selecting = false;
    selection.clear();
  };

  const choose = (action) => {
    showMenu = false;
    onAction(action);
  };

  $: count = $selection.size;
  // `>=` rather than `===` so a stale total can't strand the button on "Select All".
  $: allSelected = total > 0 && count >= total;
</script>

<div class="selection d-flex align-items-center gap-2">
  {#if selecting}
    {#if count}
      <small class="text-muted text-nowrap" data-testid="selection-count">
        {$format('label.BULK_ACTION.SELECTED_COUNT', { values: { checked: count, total } })}
      </small>
    {/if}

    <!--
      Adds the current page, so it never discards ticks made on other pages. Flips to
      Deselect All once everything matching is selected, where clearing is unambiguous.
    -->
    <Button
      id="{id}-select-all-btn"
      color="link"
      size="sm"
      class="px-1"
      on:click={() => (allSelected ? selection.clear() : onSelectAll())}
    >
      {$format(allSelected ? 'label.DESELECT_ALL' : 'label.SELECT_ALL')}
    </Button>

    <!-- `count` is guarded twice so Select All can sit between the count and Actions. -->
    {#if count}
      <Dropdown isOpen={showMenu} toggle={() => (showMenu = !showMenu)}>
        <DropdownToggle id="{id}-bulk-actions-btn" color="primary" outline caret>
          {$format('label.ACTIONS')}
        </DropdownToggle>
        <DropdownMenu end>
          <DropdownItem on:click={() => choose('reminder')}>{$format('label.SEND_REMINDER')}</DropdownItem>
          <DropdownItem on:click={() => choose('cache')}>{$format('label.CLEAR_CACHE')}</DropdownItem>
          <!-- Grouped by consequence: sends, then a download, then the destructive one. -->
          <DropdownItem divider />
          <DropdownItem on:click={() => choose('signatures')}>
            {$format('label.BULK_ACTION.DOWNLOAD_SIGNATURES')}
          </DropdownItem>
          <DropdownItem divider />
          <DropdownItem class="text-danger" on:click={() => choose('disable')}>
            {$format('label.DISABLE_USER')}
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    {/if}

    <Button on:click={cancel}>
      <Icon class="d-inline d-md-none" name="x" />
      <span class="d-none d-md-inline">{$format('label.CANCEL')}</span>
    </Button>
  {:else}
    <Button id="{id}-select-btn" on:click={enable}>
      <span>{$format('label.SELECT')}</span>
    </Button>
    <Tooltip target="{id}-select-btn" placement="top">{$format('label.SELECT')}</Tooltip>
  {/if}
</div>

<style lang="scss">
  :global(.selection .btn) {
    padding: calc(var(--padding-base) - 3px) calc(var(--padding-base) + 2px);
  }
</style>
